import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {PasswordRecoveryApiService} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, toBusinessOrNull, TypedForm} from '@npck/libs/commons';
import {PASSWORD_MAX_LENGTH} from '../../domain/constants';
import {TOKEN} from '../../domain/query-params.keys';
import {passwordRequirementsValidator} from '../../infra/password-requirements.validator';
import {passwordsMatchValidator} from '../../infra/passwords-match.validator';
import {Path} from '../../path';

const TOKEN_CODES = ['AF_PASSWORD_RECOVERY_TOKEN_INVALID', 'AF_PASSWORD_RECOVERY_TOKEN_EXPIRED'];
const PASSWORD_VALIDATION_FAILED = 'AF_PASSWORD_RECOVERY_PASSWORD_VALIDATION_FAILED';
const PASSWORD_MISMATCH = 'AF_PASSWORD_RECOVERY_PASSWORD_MISMATCH';

@Component({
    selector: 'af-reset-password',
    standalone: false,
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
    form: TypedForm<ResetPasswordForm>;
    tokenChecked = false;
    linkExpired = false;
    passwordReset = false;
    showNewPassword = false;
    showConfirmPassword = false;
    serverValidationFailed = false;
    readonly maxLength = PASSWORD_MAX_LENGTH;

    private token: string | null = null;

    constructor(
        private formBuilder: FormBuilder,
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private passwordRecoveryApi: PasswordRecoveryApiService,
    ) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.token = this.activatedRoute.snapshot.queryParamMap.get(TOKEN);
        if (!this.token) {
            this.expire();
            return;
        }
        this.passwordRecoveryApi.validatePasswordRecoveryToken({token: this.token})
            .pipe(rxSpinner('validate'))
            .subscribe({
                next: () => this.tokenChecked = true,
                error: err => {
                    const businessError = toBusinessOrNull(err);
                    if (businessError && TOKEN_CODES.includes(businessError.error)) {
                        this.expire();
                    } else {
                        throw err;
                    }
                },
            });
    }

    submit(): void {
        this.passwordRecoveryApi.resetPasswordWithToken({
            token: this.token,
            newPassword: this.form.value.newPassword,
            confirmPassword: this.form.value.confirmPassword,
        })
            .pipe(rxSpinner('submit'))
            .subscribe({
                next: () => this.passwordReset = true,
                error: err => this.handleResetError(err),
            });
    }

    cancel(): void {
        this.routerService.navigate(Path.LOGIN);
    }

    private handleResetError(err: HttpErrorResponse): void {
        const businessError = toBusinessOrNull(err);
        const code: string | undefined = businessError?.error;
        if (code && TOKEN_CODES.includes(code)) {
            this.expire();
        } else if (code === PASSWORD_VALIDATION_FAILED) {
            this.serverValidationFailed = true;
        } else if (code === PASSWORD_MISMATCH) {
            this.form.controls.confirmPassword.setErrors({passwordsMismatch: true});
            this.form.controls.confirmPassword.markAsTouched();
        } else {
            throw err;
        }
    }

    private expire(): void {
        this.tokenChecked = true;
        this.linkExpired = true;
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            newPassword: new FormControl('', [Validators.required, passwordRequirementsValidator()]),
            confirmPassword: new FormControl('', [Validators.required]),
        }, {validators: passwordsMatchValidator('newPassword', 'confirmPassword')});
        this.form.controls.newPassword.valueChanges.subscribe(() => {
            this.serverValidationFailed = false;
        });
    }
}

interface ResetPasswordForm {
    newPassword: string;
    confirmPassword: string;
}
